import {
  Array,
  Boolean,
  Literal,
  Null,
  Number,
  Optional,
  Record as RRecord,
  Static,
  String,
  Union,
} from "runtypes";

import { getNormalEnum } from "../runtypeEnums.js";

const O3Literal = Literal("O3");
export const OpenAiChatModelLiteral = Union(
  Literal("GPT_4_OMNI"),
  Literal("GPT_4_OMNI_MINI"),
  Literal("GPT_41"),
  Literal("GPT_41_MINI"),
  Literal("GPT_41_NANO"),
  O3Literal,
  Literal("GPT_5"),
  Literal("GPT_51"),
  Literal("GPT_52"),
  Literal("GPT_53_CODEX"),
  Literal("GPT_54"),
);
export type OpenAiChatModel = Static<typeof OpenAiChatModelLiteral>;
export const OpenAiChatModel = getNormalEnum(OpenAiChatModelLiteral);

export const OpenAiChatModelNameMap: Record<OpenAiChatModel, string> = {
  [OpenAiChatModel.GPT_4_OMNI]: "gpt-4o-2024-11-20",
  [OpenAiChatModel.GPT_4_OMNI_MINI]: "gpt-4o-mini-2024-07-18",
  [OpenAiChatModel.GPT_41]: "gpt-4.1-2025-04-14",
  [OpenAiChatModel.GPT_41_MINI]: "gpt-4.1-mini-2025-04-14",
  [OpenAiChatModel.GPT_41_NANO]: "gpt-4.1-nano-2025-04-14",
  [OpenAiChatModel.O3]: "o3",
  [OpenAiChatModel.GPT_5]: "gpt-5",
  [OpenAiChatModel.GPT_51]: "gpt-5.1",
  [OpenAiChatModel.GPT_52]: "gpt-5.2",
  [OpenAiChatModel.GPT_53_CODEX]: "gpt-5.3-codex",
  [OpenAiChatModel.GPT_54]: "gpt-5.4",
};

export const OpenAiReasoningModelLiteral = Union(
  O3Literal,
  Literal("GPT_5"),
  Literal("GPT_51"),
  Literal("GPT_52"),
  Literal("GPT_53_CODEX"),
  Literal("GPT_54"),
);
export type OpenAiReasoningModel = Static<typeof OpenAiReasoningModelLiteral>;
export const OpenAiReasoningModel = getNormalEnum(OpenAiReasoningModelLiteral);

/**
 * Assistant phase metadata from the OpenAI Responses API.
 */
export const OpenAIAssistantPhaseLiteral = Union(
  Literal("commentary"),
  Literal("final_answer"),
);
export type OpenAIAssistantPhase = Static<typeof OpenAIAssistantPhaseLiteral>;
export const isOpenAIAssistantPhase = (
  phase: unknown,
): phase is OpenAIAssistantPhase =>
  phase === "commentary" || phase === "final_answer";

export const OPENAI_DEFAULT_EXECUTOR_FINE_TUNED_MODEL =
  "ft:gpt-4o-mini-2024-07-18:hex-internal:qualified-paths:BHC3vYCF";

// OpenAI documentation of how they count tokens seems to be kinda off, so
// for now we'll just subtract 10 from the max tokens to be safe.
export const OPENAI_CHAT_MODEL_DETAILS: Record<
  OpenAiChatModel,
  {
    maxInputTokens: number;
    maxOutputTokens: number;
    tokensPerMessage: number;
  }
> = {
  [OpenAiChatModel.GPT_4_OMNI]: {
    maxInputTokens: 128000 - 4096 - 10,
    maxOutputTokens: 4096,
    tokensPerMessage: 3,
  },
  [OpenAiChatModel.GPT_4_OMNI_MINI]: {
    maxInputTokens: 128000 - 16384 - 10,
    maxOutputTokens: 16384,
    tokensPerMessage: 3,
  },
  [OpenAiChatModel.GPT_41_MINI]: {
    maxInputTokens: 128000 - 16384 - 10,
    maxOutputTokens: 16384,
    tokensPerMessage: 3,
  },
  [OpenAiChatModel.GPT_41_NANO]: {
    maxInputTokens: 128000 - 16384 - 10,
    maxOutputTokens: 16384,
    tokensPerMessage: 3,
  },
  [OpenAiChatModel.GPT_41]: {
    maxInputTokens: 400000 - 16384 - 10,
    maxOutputTokens: 16384,
    tokensPerMessage: 3,
  },
  [OpenAiChatModel.O3]: {
    maxInputTokens: 200000 - 16384 - 10,
    maxOutputTokens: 16384,
    tokensPerMessage: 3,
  },
  [OpenAiChatModel.GPT_5]: {
    maxInputTokens: 400000 - 16384 - 10,
    maxOutputTokens: 16384,
    tokensPerMessage: 3,
  },
  [OpenAiChatModel.GPT_51]: {
    maxInputTokens: 400000 - 128000 - 10,
    maxOutputTokens: 128000,
    tokensPerMessage: 3,
  },
  [OpenAiChatModel.GPT_52]: {
    maxInputTokens: 400000 - 128000 - 10,
    maxOutputTokens: 128000,
    tokensPerMessage: 3,
  },
  [OpenAiChatModel.GPT_53_CODEX]: {
    maxInputTokens: 400000 - 128000 - 10,
    maxOutputTokens: 128000,
    tokensPerMessage: 3,
  },
  [OpenAiChatModel.GPT_54]: {
    maxInputTokens: 1000000 - 128000 - 10,
    maxOutputTokens: 128000,
    tokensPerMessage: 3,
  },
};

// These params are not allowed to be set for reasoning models
// The API will return a 400 if they are
export const openAiReasoningModelChatCompletionParams = {
  temperature: undefined,
  parallel_tool_calls: undefined,
};
export const CustomOpenAiModelConfig = RRecord({
  model: String,
  maxInputTokens: Number,
  maxOutputTokens: Optional(Number), // Not used by reasoning models
});
export type CustomOpenAiModelConfig = Static<typeof CustomOpenAiModelConfig>;

export const OpenAiModelParams = RRecord({
  frequencyPenalty: Number,
  n: Number,
  presencePenalty: Number,
  stopWords: Array(String),
  temperature: Optional(Number), // Not used by reasoning models
  topP: Number,
  reasoningEffort: Optional(
    Union(
      Literal("none"),
      Literal("minimal"),
      Literal("low"),
      Literal("medium"),
      Literal("high"),
      Literal("xhigh"),
    ),
  ), // ONLY used by reasoning models
  verbosity: Optional(
    Union(Literal("low"), Literal("medium"), Literal("high")),
  ),
  model: Union(OpenAiChatModelLiteral, CustomOpenAiModelConfig),
});
export type OpenAiModelParams = Static<typeof OpenAiModelParams>;

export const OpenAiCompactionConfig = RRecord({
  enabled: Boolean,
  compactThreshold: Optional(Number),
});
export type OpenAiCompactionConfig = Static<typeof OpenAiCompactionConfig>;

// Based on `ChatCompletionRequestMessageRoleEnum` from openai package
export const OpenAiChatMessageRoleLiteral = Union(
  Literal("system"),
  Literal("user"),
  Literal("assistant"),
  Literal("tool"),
);
export type OpenAiChatMessageRole = Static<typeof OpenAiChatMessageRoleLiteral>;
export const OpenAiChatMessageRole = getNormalEnum(
  OpenAiChatMessageRoleLiteral,
);

// based on `ChatCompletionContentPartImage` from openai package
const OpenAiChatContentPartImage = RRecord({
  type: Literal("image_url"),
  image_url: RRecord({
    url: String,
  }),
});

// based on `ChatCompletionContentPartFile` from openai package
// See https://developers.openai.com/api/docs/guides/pdf-files/
const OpenAiChatContentPartFile = RRecord({
  type: Literal("file"),
  file: RRecord({
    filename: String,
    file_data: String, // base64-encoded file with data URI prefix
  }),
});

const OpenAiChatContentPart = Union(
  OpenAiChatContentPartImage,
  OpenAiChatContentPartFile,
);

// Based on `ChatCompletionRequestMessage` from openai package
export const OpenAiChatMessage = Union(
  RRecord({
    content: String,
    role: Literal(OpenAiChatMessageRole.system),
  }),
  RRecord({
    content: String.Or(Array(OpenAiChatContentPart)),
    role: Literal(OpenAiChatMessageRole.user),
  }),
  RRecord({
    content: String.Or(Null),
    // OpenAI requires snake case
    function_call: Optional(RRecord({ arguments: String, name: String })),
    role: Literal(OpenAiChatMessageRole.assistant),
  }),
  RRecord({
    content: String,
    role: Literal(OpenAiChatMessageRole.tool),
    tool_call_id: String,
  }),
  RRecord({
    tool_calls: Array(
      RRecord({
        type: Literal("function"),
        id: String,
        function: RRecord({ name: String, arguments: String }),
      }),
    ),
    role: Literal(OpenAiChatMessageRole.assistant),
  }),
);
export type OpenAiChatMessage = Static<typeof OpenAiChatMessage>;

export const OpenAIConformerResult = RRecord({
  messages: Array(OpenAiChatMessage),
});
export type OpenAIConformerResult = Static<typeof OpenAIConformerResult>;

/**
 * Determines if a model name corresponds to a reasoning model
 * Reasoning models use max_completion_tokens instead of max_tokens
 */
export function isReasoningModel(modelName: string): boolean {
  // Check if it's a reasoning model based on naming patterns
  const reasoningModelPatterns = [
    /^o1/i, // o1-preview, o1-mini, etc.
    /^o3/i, // o3-mini, o3, etc.
    /^gpt-5/i, // gpt-5 models
    /gpt-4\.1/i, // gpt-4.1 models
    /^robin-alpha/i, // robin alpha reasoning model, temp
  ];

  return reasoningModelPatterns.some((pattern) => pattern.test(modelName));
}
